import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  clearAuthToken,
  readAuthToken,
  subscribeAuthTokenChanges,
  writeAuthToken,
} from "./auth-storage";

interface AuthTokenContextValue {
  token: string | undefined;
  setToken: (token: string) => void;
  clear: () => void;
}

const AuthTokenContext = createContext<AuthTokenContextValue | undefined>(
  undefined,
);

export function AuthTokenProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState(() => readAuthToken());

  const setToken = useCallback((next: string) => {
    writeAuthToken(next);
    setTokenState(next);
  }, []);

  const clear = useCallback(() => {
    clearAuthToken();
    setTokenState(undefined);
  }, []);

  useEffect(() => subscribeAuthTokenChanges(setTokenState), []);

  const value = useMemo(
    () => ({ token, setToken, clear }),
    [token, setToken, clear],
  );
  return (
    <AuthTokenContext.Provider value={value}>
      {children}
    </AuthTokenContext.Provider>
  );
}

export function useAuthToken(): AuthTokenContextValue {
  const ctx = useContext(AuthTokenContext);
  if (!ctx) {
    throw new Error("useAuthToken must be used within AuthTokenProvider");
  }
  return ctx;
}
